import {Trans} from '@lingui/macro';
import {noop} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useNavigationBlocking} from '../../../srcCommon/app/navigationBlocking';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {DateTimePopoverContainer} from '../../components/common/createFormInputs/dateTimePopoverContainer';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {SettingsComposerContainer} from '../../components/inboxes/composer/settings/settingsComposerContainer';
import {formatDate, patchDateTimeFormat} from '../../helpers/dateTimeHelpers';
import {findInlineAttachmentsInHtml} from '../../helpers/models/messageHelpers';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {useVacationResponderEditState} from '../../helpers/vacationResponderHelpers';
import {VacationResponderModel} from '../../models/api/vacationResponderModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsVacationRespondersUrl} from '../../models/url/settingsUrlModel';
import {getUserOwnedChannels} from '../../store/data/channels/channelsSelectors';
import {getUser} from '../../store/data/teammates/teammatesSelectors';
import {
  createVacationResponderAsyncAction,
  deleteVacationResponderAsyncAction,
  saveVacationResponderAsyncAction,
} from '../../store/data/vacationResponders/saveVacationRespondersActions';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {SettingsDiscardChangesDialog} from '../common/settingsDiscardChangesDialog';
import {SettingsFieldLine} from '../common/settingsFieldLine';
import {SettingsFieldSection} from '../common/settingsFieldSection';
import {SettingsFooter, StyledSettingsMain} from '../common/settingsWrapper';
import {SettingsPreferencesToggleItem} from '../preferences/items/settingsPreferencesToggleItem';
import {settingsHorizontalPaddingStyles} from '../settingsCommonConstants';
import {SettingsHeader} from '../settingsHeader';

/*
 * Constants.
 */

const enableTitle = <Trans id="vacation-responders-form-enable-title">Enable</Trans>;
const enableSubtitle = (
  <Trans id="vacation-responders-form-enable-subtitle">
    The autoresponder is enabled if assigned to one or more channels
  </Trans>
);
const onlySendToContactsTitle = (
  <Trans id="vacation-responders-form-onlysendtocontacts-title">Only send to contacts</Trans>
);
const onlySendToContactsSubtitle = (
  <Trans id="vacation-responders-form-onlysendtocontacts-subtitle">
    Auto-responses will only be sent to people in your contacts
  </Trans>
);

const vacationRespondersFormSectionMaxWidth = 768;

/*
 * Types.
 */

export interface SettingsVacationResponderFormProps {
  panelId: SettingsPanelId;
  vacationRespondersUrl: SettingsVacationRespondersUrl;
  initialVacationResponder: VacationResponderModel | undefined;
}

/*
 * Style.
 */

export const StyledSettingsFormContainerDiv = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;
  position: relative;
`;

export const StyledSettingsContentWrap = styled.div`
  flex: 1;
  margin-bottom: 48px;
  ${settingsHorizontalPaddingStyles};
  display: flex;
  padding-right: 0;
`;

export const StyledSettingsContent = styled.div`
  flex: 1;
  min-height: min-content;
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: min-content;
  grid-row-gap: 24px;
`;

export const StyledSettingsField = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledInputWrap = styled.div`
  width: 300px;
`;

const StyledInputRowWrapperDiv = styled.div`
  display: flex;
  gap: 24px;
`;

const StyledToggleFieldLine = styled(SettingsFieldLine)`
  div:first-child > & {
    border-top: 0 none;
  }

  max-width: ${vacationRespondersFormSectionMaxWidth}px;
`;

const StyledEnableFieldLine = styled(StyledToggleFieldLine)`
  padding-bottom: 8px;
`;

const StyledOnlySendToContactsFieldLine = styled(StyledToggleFieldLine)`
  padding-bottom: 24px;
`;

const StyledMandatoryLabelDiv = styled.div`
  display: flex;
`;

const StyledMandatoryAsterisk = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
`;

/*
 * Component.
 */

export const SettingsVacationRespondersForm: FC<SettingsVacationResponderFormProps> = (props) => {
  const {panelId, vacationRespondersUrl, initialVacationResponder} = props;
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();

  const user = useAppSelector(getUser);
  const channels = useAppSelector(getUserOwnedChannels);

  const {vacationResponderId} = vacationRespondersUrl;

  const {
    isEditing,
    isSaveEnabled,
    vacationResponder,
    onNameChange,
    onEnabledChange,
    onChannelSwitch,
    onSendToContactsChange,
    onStartDateWebChange,
    onEndDateWebChange,
    onBodyChange,
    onBodyEmptyChange,
    onAttachmentUploaded,
    onRemoveAttachmentPressWeb,
  } = useVacationResponderEditState({
    user,
    vacationResponderId,
    channels,
    initialVacationResponder,
  });

  const [isSaving, setIsSaving] = useState(false);

  const canSave = useMemo(() => !isSaving && isSaveEnabled, [isSaveEnabled, isSaving]);

  const [shouldDisplayDeleteDialog, setShouldDisplayDeleteDialog] = useState(false);

  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, canSave);

  const navigateToVacationRespondersOverview = () => {
    const newUrl = buildSettingsPath({
      ...vacationRespondersUrl,
      resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      vacationResponderId: undefined,
    });
    navigateToLink(newUrl);
  };

  const unblockAndNavigateToVacationResponders = () => {
    unblockNavigation();
    navigateToVacationRespondersOverview();
  };

  const computeFilteredAttachments = () => {
    const attachmentCidsInBody = findInlineAttachmentsInHtml(vacationResponder.body).map((a) => a.cid);

    // if attachment is not in body, but isInline is true, filter it out.
    return vacationResponder.attachments.filter((a) => {
      if (a.isInline) {
        return attachmentCidsInBody.includes(a.cid);
      }

      return true;
    });
  };

  const onCreate = async () => {
    setIsSaving(true);
    try {
      await dispatch(
        createVacationResponderAsyncAction({...vacationResponder, attachments: computeFilteredAttachments()}),
      );
      unblockAndNavigateToVacationResponders();
    } catch (e) {
      reportToBugsnag(e);
    }
    setIsSaving(false);
  };

  const onSave = async () => {
    setIsSaving(true);
    try {
      if (vacationResponderId) {
        await dispatch(
          saveVacationResponderAsyncAction(vacationResponderId, {
            ...vacationResponder,
            attachments: computeFilteredAttachments(),
          }),
        );
      }
      unblockAndNavigateToVacationResponders();
    } catch (e) {
      reportToBugsnag(e);
    }
    setIsSaving(false);
  };

  const renderTopLine = () => {
    const breadcrumbs: ReadonlyArray<FolderBreadcrumb> = [];

    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: <Trans id="settings-vacation-responders">Vacation responders</Trans>,
          onBreadcrumbClick: navigateToVacationRespondersOverview,
        }}
        breadcrumbs={breadcrumbs}
      />
    );
  };

  const startTimeTitle = (
    <StyledMandatoryLabelDiv>
      <Trans id="start-time-vacation-responders-title">Start time</Trans>
      <StyledMandatoryAsterisk>*</StyledMandatoryAsterisk>
    </StyledMandatoryLabelDiv>
  );

  const endTimeTitle = <Trans id="end-time-vacation-responders-title">End time</Trans>;

  const nameLabel = (
    <StyledMandatoryLabelDiv>
      <Trans id="settings-vacation-responder-form-name">Name</Trans>
      <StyledMandatoryAsterisk>*</StyledMandatoryAsterisk>
    </StyledMandatoryLabelDiv>
  );

  const renderBody = () => (
    <>
      <StyledSettingsField>
        <StyledInputWrap>
          <TextBox
            label={nameLabel}
            onChange={(newName) => onNameChange(newName)}
            value={vacationResponder.name}
            placeholder="Add name"
          />
        </StyledInputWrap>
        <StyledInputRowWrapperDiv>
          <DateTimePopoverContainer
            key="start-time-vacation-responders-form"
            title={startTimeTitle}
            value={vacationResponder.startAt.toString()}
            onSelect={onStartDateWebChange}
            placeholder={formatDate(
              (dateTime) => dateTime.toLocaleString(patchDateTimeFormat({dateStyle: 'medium'})),
              DateTime.now(),
              {dateStyle: 'medium'},
            )}
            customFormat={{dateStyle: 'medium', timeStyle: 'short'}}
            shouldShowTimeZone
          />
          <DateTimePopoverContainer
            key="end-time-vacation-responders-form"
            title={endTimeTitle}
            value={vacationResponder.endAt?.toString() || ''}
            onSelect={onEndDateWebChange}
            shouldShowClearInDropdown
            placeholder="mm/dd/yyyy, hh:mm"
            customFormat={{dateStyle: 'medium', timeStyle: 'short'}}
            shouldAllowNullDate
            minDateTime={DateTime.fromMillis(vacationResponder.startAt)}
            shouldSetUndefinedDefaultDateTime={!vacationResponder.endAt}
            isTimeFieldReadOnly
            shouldShowTimeZone
            onDelete={() => onEndDateWebChange('')}
          />
        </StyledInputRowWrapperDiv>
      </StyledSettingsField>
      <StyledSettingsField>
        <StyledEnableFieldLine
          title={enableTitle}
          subtitle={enableSubtitle}
          content={
            <SettingsPreferencesToggleItem
              title={enableTitle}
              description={enableSubtitle}
              defaultValue
              value={vacationResponder.isEnabled}
              updatePreference={onEnabledChange}
            />
          }
        />
        {channels.map((channel) => (
          <SettingsFieldSection title="">
            <CheckBox
              key={channel.id}
              isChecked={vacationResponder.channelIds.includes(channel.id)}
              onChange={(isChecked) => onChannelSwitch(channel, isChecked)}
            >
              <Trans id="settings-vacation-responder-form-channel-name">{channel.displayName}</Trans>
            </CheckBox>
          </SettingsFieldSection>
        ))}
      </StyledSettingsField>
      <StyledSettingsField>
        <StyledOnlySendToContactsFieldLine
          title={onlySendToContactsTitle}
          subtitle={onlySendToContactsSubtitle}
          content={
            <SettingsPreferencesToggleItem
              title={onlySendToContactsTitle}
              description={onlySendToContactsSubtitle}
              defaultValue
              value={vacationResponder.onlyContacts}
              updatePreference={onSendToContactsChange}
            />
          }
        />
        <SettingsComposerContainer
          initialCleanHtml={vacationResponder.body}
          onChange={(text, isEmpty) => {
            onBodyEmptyChange(isEmpty);
            onBodyChange(text);
          }}
          attachments={vacationResponder.attachments}
          onAddAttachment={onAttachmentUploaded}
          onRemoveAttachment={onRemoveAttachmentPressWeb}
          canEditRawHtml
          onVariableInserted={noop}
          placeholder={
            <Trans id="settings-vacations-responders-form-placeholder">Type your message here.</Trans>
          }
          shouldHideCsatSurveysButton
        />
      </StyledSettingsField>
    </>
  );

  const renderFooter = () => {
    if (isEditing && !canSave) {
      return null;
    }

    return (
      <SettingsFooter>
        <Button
          isRounded
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={navigateToVacationRespondersOverview}
        >
          <Trans id="settings-vacations-responder-form-cancel">Cancel</Trans>
        </Button>
        <Button
          isRounded
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={isEditing ? onSave : onCreate}
          isDisabled={!canSave}
        >
          {isEditing ? (
            <Trans id="settings-vacations-responder-form-save">Save</Trans>
          ) : (
            <Trans id="settings-vacations-responder-form-create">Create</Trans>
          )}
        </Button>
      </SettingsFooter>
    );
  };

  const maybeRenderDeleteDialog = () => {
    if (!isEditing || !shouldDisplayDeleteDialog) {
      return null;
    }

    const onDelete = async () => {
      dispatch(deleteVacationResponderAsyncAction(vacationResponder.id));
      unblockAndNavigateToVacationResponders();
    };

    return (
      <OkCancelDialog
        title="Delete this vacation responder?"
        okButtonLabel={<Trans id="delete-vacation-responder-confirmation-dialog">Delete responder</Trans>}
        okButtonVariant={ButtonVariantsEnum.DANGER}
        onRequestClose={() => setShouldDisplayDeleteDialog(false)}
        onOkClick={onDelete}
      >
        This action cannot be undone.
      </OkCancelDialog>
    );
  };

  const maybeRenderRightActions = () => {
    if (!isEditing) {
      return null;
    }

    return (
      <Button
        isRounded
        variant={ButtonVariantsEnum.SECONDARY_DANGER}
        onClick={() => setShouldDisplayDeleteDialog(true)}
      >
        <Trans id="settings-vacation-responders-edit-form-delete-action">Delete vacation responder</Trans>
      </Button>
    );
  };

  return (
    <StyledSettingsFormContainerDiv>
      <SettingsHeader topLine={renderTopLine()} right={maybeRenderRightActions()}>
        {isEditing ? (
          <>{vacationResponder.name}</>
        ) : (
          <Trans id="settings-vacation-responders-form-add-action">Add vacation responder</Trans>
        )}
      </SettingsHeader>
      <StyledSettingsMain>
        <StyledSettingsContentWrap>
          <StyledSettingsContent>{renderBody()}</StyledSettingsContent>
        </StyledSettingsContentWrap>
        {maybeRenderDeleteDialog()}
        {didBlockNavigation ? (
          <SettingsDiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />
        ) : null}
      </StyledSettingsMain>
      {renderFooter()}
    </StyledSettingsFormContainerDiv>
  );
};
